import React, { useRef } from "react";

import { Box, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import type { ReportMeta } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { EditableField } from "./EditableField";

interface ReportHeaderProps {
  meta: ReportMeta;
  onUpdate: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({ meta, onUpdate, onLogoFileChange }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const dateFormat = useDateFormatSelect();

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpdate({ logo: reader.result as string });
    reader.readAsDataURL(file);
    onLogoFileChange(file);
    event.target.value = "";
  };

  return (
    <Box sx={{ bgcolor: "#071f35", p: "28px 28px 24px", position: "relative" }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />

      <Typography
        sx={{
          position: "absolute",
          top: 8,
          right: 12,
          fontSize: 8,
          fontWeight: 600,
          color: "rgba(255,255,255,0.3)",
          letterSpacing: "0.04em"
        }}
      >
        Click to edit
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ position: "relative", width: 52, height: 52, flexShrink: 0, "&:hover .logo-remove": { opacity: 1 } }}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              fileRef.current?.click();
            }}
            title={meta.logo ? "Change logo" : "Upload logo"}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              border: meta.logo ? "none" : "1px dashed rgba(255,255,255,0.3)",
              bgcolor: meta.logo ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden"
            }}
          >
            {meta.logo ? (
              <Box
                component="img"
                src={meta.logo}
                alt="Logo"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 700 }}>LOGO</Typography>
            )}
          </Box>
          {meta.logo && (
            <Box
              className="logo-remove"
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ logo: "" });
                onLogoFileChange(null);
              }}
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "none",
                bgcolor: "#DC2626",
                color: "#FFFFFF",
                fontSize: 9,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 150ms ease-out",
                zIndex: 1
              }}
            >
              {"\u2715"}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: "rgba(255,255,255,0.5)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: "6px"
            }}
          >
            Portfolio Report
          </Typography>
          <EditableField
            value={meta.title}
            placeholder="Report title"
            onSave={(v) => onUpdate({ title: v })}
            maxLength={100}
            style={{ fontSize: 22, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.25 }}
          />
          <EditableField
            value={meta.subtitle}
            placeholder="Add subtitle"
            onSave={(v) => onUpdate({ subtitle: v })}
            maxLength={150}
            style={{ fontSize: 13, color: "rgba(255,255,255,0.68)", marginTop: 4 }}
          />
        </Box>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: "3px" }}>
            As of Date
          </Typography>
          <DesktopDatePicker
            format={dateFormat}
            value={meta.asOfDate ? dayjs(meta.asOfDate) : null}
            onChange={(value) => onUpdate({ asOfDate: value ? toIsoMidday(value) : "" })}
            slotProps={{
              textField: {
                variant: "standard",
                placeholder: "Select date",
                InputProps: { disableUnderline: true },
                sx: {
                  "& .MuiInputBase-input": {
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#FFFFFF",
                    textAlign: "right",
                    p: 0,
                    cursor: "pointer"
                  },
                  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.55)", fontSize: 16 }
                }
              },
              popper: { sx: { zIndex: 1500 } }
            }}
          />
          <EditableField
            value={meta.preparedBy}
            placeholder="Prepared by"
            onSave={(v) => onUpdate({ preparedBy: v })}
            maxLength={80}
            style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 3, textAlign: "right" }}
          />
        </Box>
      </Box>
    </Box>
  );
};
